import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { TrendingUp, Package, Edit2, Save, X, StickyNote, Calendar, Clock } from 'lucide-react';
import TaskCard from './TaskCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Targets = ({ user }) => {
  const [targets, setTargets] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [showNotesModal, setShowNotesModal] = useState(null);
  const [notesContent, setNotesContent] = useState('');

  const fetchTargets = async () => {
    try {
      const response = await axios.get(`${API}/targets`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      // Filter out project cards (they have their own panel now)
      const nonProjectTargets = response.data.filter(target => target.target_type !== 'project');
      setTargets(nonProjectTargets);
    } catch (error) {
      console.error('Failed to fetch targets');
    }
  };

  const fetchTotalProducts = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      setTotalProducts(response.data.total_products || 0);
    } catch (error) {
      console.error('Failed to fetch total products');
    }
  };

  useEffect(() => {
    fetchTargets();
    fetchTotalProducts();
  }, []);

  const handleUpdate = async (targetId, updateData = null) => {
    try {
      const dataToUpdate = updateData || editData;
      console.log('Updating target:', targetId, 'with data:', dataToUpdate);
      const response = await axios.put(`${API}/targets/${targetId}`, dataToUpdate, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      console.log('Update response:', response.data);
      if (!updateData) {
        toast.success('Target updated successfully');
        setEditingId(null);
      }
      fetchTargets();
    } catch (error) {
      console.error('Update error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update target';
      toast.error(errorMsg);
      throw error;
    }
  };

  const handleEdit = (target) => {
    console.log('Edit clicked for target:', target.id);
    setEditingId(target.id);
    setEditData({
      name: target.name || '',
      target_value: target.target_value || 0,
      achieved_value: target.achieved_value || 0,
      notes: target.notes || '',
      deadline_date: target.deadline_date || ''
    });
  };

  const calculateDaysRemaining = (deadlineDate) => {
    if (!deadlineDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const deadline = new Date(deadlineDate);
    deadline.setHours(0, 0, 0, 0);
    
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const openNotesModal = (target) => {
    setShowNotesModal(target.id);
    setNotesContent(target.notes || '');
  };

  const saveNotes = async (targetId) => {
    try {
      await axios.put(`${API}/targets/${targetId}`, { notes: notesContent }, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      toast.success('Notes saved');
      setShowNotesModal(null);
      fetchTargets();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const getTargetCard = (target) => {
    let remaining = 0;
    let percentage = 0;
    let subtitle = '';

    if (target.target_type === 'yearly_sales') {
      remaining = target.target_value - target.achieved_value;
      percentage = target.target_value > 0 ? (target.achieved_value / target.target_value) * 100 : 0;
      subtitle = `₹${target.achieved_value.toLocaleString()} achieved • ₹${remaining.toLocaleString()} remaining`;
    } else if (target.target_type === 'product_target') {
      const achieved = totalProducts;
      remaining = target.target_value - achieved;
      percentage = target.target_value > 0 ? (achieved / target.target_value) * 100 : 0;
      subtitle = `${achieved} products • ${remaining > 0 ? remaining : 0} to achieve`;
    } else {
      remaining = target.target_value - target.achieved_value;
      percentage = target.target_value > 0 ? (target.achieved_value / target.target_value) * 100 : 0;
      subtitle = `${target.achieved_value.toLocaleString()} / ${target.target_value.toLocaleString()}`;
    }

    return (
      <div key={target.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all p-6 border border-slate-200">
        {editingId === target.id ? (
          <div className="space-y-4">
            <input
              type="text"
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-lg font-semibold"
              placeholder="Target Name"
            />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600 block mb-1">Target Value</label>
                <input
                  type="number"
                  value={editData.target_value}
                  onChange={(e) => setEditData({ ...editData, target_value: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 block mb-1">Achieved Value</label>
                <input
                  type="number"
                  value={editData.achieved_value}
                  onChange={(e) => setEditData({ ...editData, achieved_value: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  disabled={target.target_type === 'product_target'}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Deadline Date
              </label>
              <input
                type="date"
                value={editData.deadline_date || ''}
                onChange={(e) => setEditData({ ...editData, deadline_date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={() => handleUpdate(target.id)} size="sm">
                <Save className="w-4 h-4 mr-1" /> Save
              </Button>
              <Button onClick={() => setEditingId(null)} size="sm" variant="outline">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-bold text-slate-800">{target.name}</h3>
                  {target.deadline_date && target.deadline_date.trim() !== '' && (() => {
                    const daysRemaining = calculateDaysRemaining(target.deadline_date);
                    return (
                      <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                        daysRemaining < 0 ? 'bg-red-100 text-red-700' :
                        daysRemaining <= 7 ? 'bg-orange-100 text-orange-700' :
                        daysRemaining <= 30 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        <Clock className="w-3 h-3" />
                        {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` :
                         daysRemaining === 0 ? 'Due today' :
                         daysRemaining === 1 ? '1 day left' :
                         `${daysRemaining} days left`}
                      </div>
                    );
                  })()}
                </div>
                <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
                {target.deadline_date && target.deadline_date.trim() !== '' && (
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Deadline: {new Date(target.deadline_date).toLocaleDateString('en-IN', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {(target.target_type === 'custom_1' || target.target_type === 'custom_2') && (
                  <Button onClick={() => openNotesModal(target)} size="sm" variant="ghost">
                    <StickyNote className="w-4 h-4" />
                  </Button>
                )}
                <Button onClick={() => handleEdit(target)} size="sm" variant="ghost">
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="mb-2">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-600">Progress</span>
                <span className="font-semibold text-slate-800">{Math.min(percentage, 100).toFixed(1)}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all"
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
            </div>

            {target.target_type === 'yearly_sales' && (
              <div className="text-3xl font-bold text-indigo-600 mt-3">
                ₹{target.target_value.toLocaleString()}
              </div>
            )}
            {target.target_type === 'product_target' && (
              <div className="text-3xl font-bold text-green-600 mt-3">
                {target.target_value} Products
              </div>
            )}
            
            {/* Notes display for custom targets */}
            {(target.target_type === 'custom_1' || target.target_type === 'custom_2') && target.notes && (
              <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg group relative">
                <div className="flex items-start gap-2">
                  <StickyNote className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 overflow-hidden">
                    <p className="text-sm text-slate-700 line-clamp-3">
                      {target.notes}
                    </p>
                  </div>
                </div>
                
                {/* Tooltip on hover - shows full note */}
                {target.notes.length > 100 && (
                  <div className="absolute left-0 right-0 top-full mt-2 p-4 bg-white border-2 border-indigo-400 rounded-lg shadow-xl z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 max-h-64 overflow-y-auto">
                    <div className="flex items-start gap-2">
                      <StickyNote className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-indigo-900 mb-2">Full Note:</p>
                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                          {target.notes}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-slate-800">Sales & Product Targets</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {targets.map((target) => {
          // Use TaskCard for custom targets (task-based cards)
          if (target.target_type === 'custom_1' || target.target_type === 'custom_2') {
            return <TaskCard key={target.id} target={target} onUpdate={handleUpdate} />;
          }
          // Use regular target card for yearly_sales and product_target
          return getTargetCard(target);
        })}
      </div>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowNotesModal(null)}>
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              <StickyNote className="w-5 h-5 text-indigo-600" />
              Target Notes
            </h3>
            <textarea
              value={notesContent}
              onChange={(e) => setNotesContent(e.target.value)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg h-80 resize-y focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors"
              placeholder="Write detailed notes about this target...&#10;&#10;You can add:&#10;• Goals and objectives&#10;• Progress updates&#10;• Challenges and solutions&#10;• Any important information"
            />
            <p className="text-xs text-slate-500 mt-2">
              {notesContent.length} characters • Hover over the note on the card to see full content
            </p>
            <div className="flex gap-2 mt-4">
              <Button onClick={() => saveNotes(showNotesModal)} size="sm">
                <Save className="w-4 h-4 mr-2" /> Save Notes
              </Button>
              <Button onClick={() => setShowNotesModal(null)} size="sm" variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Targets;
